import { AccessTimeOutlined } from "@mui/icons-material";
import { Avatar, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../../common/contexts/userContext";
import { getLeagueById, getLeagueRanking } from "../../services/leagueService";
import { getLastUpdate } from "../../services/scoreService";
import { League } from "../../types/League";
import { RankingData } from "../../types/RankingData";
import LeaguesDropdown, {
    LeagueDescription,
    getGeneralLeague,
} from "../components/LeaguesDropdown";
import MatchdaySummary from "./MatchdaySummary";
import "./MobileRanking.scss";
import MobileRankingTable from "./MobileRankingTable";
import RankingTableSkeleton from "./RankingTableSkeleton";

export { getLeagueRanking as fetchLeagueInfo };

export const MobileRanking = () => {
  const { state } = useLocation();
  const { user } = useUser();
  const [lastUpdate, setLastUpdate] = useState("");
  const [currentLeagueAvatar, setCurrentLeagueAvatar] = useState("");
  const [leagueToDisplay, setLeagueToDisplay] = useState<LeagueDescription>(() =>
    state && state.leagueOption
      ? state.leagueOption
      : { leagueId: -1, label: "General" }
  );
  const [rankingData, setRankingData] = useState<RankingData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isContentReady, setIsContentReady] = useState(false);

  useEffect(() => {
    if (leagueToDisplay.leagueId >= 0) return;

    let cancelled = false;
    setIsLoading(true);
    setIsContentReady(false);

    getGeneralLeague()
      .then((data) => {
        if (cancelled) return;
        setLeagueToDisplay({ leagueId: data.id, label: "General" });
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoading(false);
        setIsContentReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [leagueToDisplay.leagueId]);

  useEffect(() => {
    if (leagueToDisplay.leagueId < 0) return;

    let cancelled = false;
    setIsLoading(true);
    setIsContentReady(false);

    Promise.allSettled([
      getLeagueRanking(leagueToDisplay.leagueId),
      getLeagueById(leagueToDisplay.leagueId),
    ]).then(([rankingResult, leagueResult]) => {
      if (cancelled) return;

      if (rankingResult.status === "fulfilled") {
        setRankingData((rankingResult.value.rankingRows ?? []) as RankingData[]);
      } else {
        setRankingData([]);
      }

      if (leagueResult.status === "fulfilled") {
        setCurrentLeagueAvatar(leagueResult.value.leagueImageUrl ?? "");
      } else {
        setCurrentLeagueAvatar("");
      }

      requestAnimationFrame(() => {
        if (cancelled) return;
        setIsLoading(false);
        setIsContentReady(true);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [leagueToDisplay.leagueId]);

  useEffect(() => {
    getLastUpdate().then(setLastUpdate);
  }, []);

  return (
    <div className="mobileRanking__root">
      <header className="mobileRanking__header">
        <div className="mobileRanking__identity">
          <Avatar
            src={currentLeagueAvatar || undefined}
            className="mobileRanking__avatar"
          >
            {leagueToDisplay.label?.charAt(0)}
          </Avatar>
          <div className="mobileRanking__titles">
            <span className="mobileRanking__eyebrow">League standings</span>
            <Typography variant="h5" className="mobileRanking__title">
              {`${leagueToDisplay.label} League`}
            </Typography>
            {lastUpdate && (
              <span className="mobileRanking__updated">
                <AccessTimeOutlined fontSize="small" />
                Updated {formatDateUpdate(lastUpdate)}
              </span>
            )}
          </div>
        </div>

        <LeaguesDropdown
          setCurrentLeague={setLeagueToDisplay}
          leagueOptions={(user.leagues ?? []).map((item: League) => ({
            label: item.name,
            leagueId: item.id,
          }))}
          currentLeague={leagueToDisplay.label}
          width="100%"
        />
      </header>

      <MatchdaySummary leagueId={leagueToDisplay.leagueId} />

      {isLoading || !isContentReady ? (
        <RankingTableSkeleton />
      ) : (
        <div className="mobileRanking__contentReady">
          <MobileRankingTable leagueRankingData={rankingData} />
        </div>
      )}
    </div>
  );
};

export { getLastUpdate };

// The backend returns a naive-UTC timestamp (no zone), matching the rest of the
// app (e.g. BetCard appends "Z"). Parse it as UTC, then format in the viewer's
// local time — so it's correct in every time zone instead of a fixed +2h (which
// was Poland-only and broke under DST).
export function formatDateUpdate(utcDateString: string) {
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(utcDateString);
  const date = new Date(hasZone ? utcDateString : `${utcDateString}Z`);
  if (Number.isNaN(date.getTime())) return utcDateString;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}
